import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import {
  Users, ShoppingCart, QrCode, TrendingUp, Eye, MessageCircle,
  Package, Settings, Lightbulb, Truck, BarChart3, RefreshCw,
  Search, ChevronLeft, ChevronRight, Pencil, Trash2, Check, X,
  ToggleLeft, ToggleRight, ArrowUpDown, DollarSign, ScanLine, Building2, Download, Type, Printer, Plus,
  Key, Car, Dog, Baby, Smartphone, MapPin, Shield, Heart, Globe, Hotel, GraduationCap, Image, Rocket, Gift, Copy, Mail, CheckCircle, Archive
} from 'lucide-react';
import { useAuth, API } from '../App';
import { categoryLabel, allCategories } from '../i18n';
import Header from '../components/Header';
import { toast } from 'sonner';
import DeployTab from './DeployTab';
import TranslationsTab from './TranslationsTab';
import OrganizationsTab from './OrganizationsTab';


// ===================== TAB NAVIGATION =====================
const tabs = [
  { id: 'overview', label: 'Vue d\'ensemble', icon: BarChart3 },
  { id: 'users', label: 'Utilisateurs', icon: Users },
  { id: 'orders', label: 'Commandes', icon: ShoppingCart },
  { id: 'products', label: 'Produits', icon: Package },
  { id: 'pingmes', label: 'PingMes', icon: QrCode },
  { id: 'organizations', label: 'Organisations', icon: Building2 },
  { id: 'generate', label: 'Plaquettes', icon: QrCode },
  { id: 'suggestions', label: 'Suggestions', icon: Lightbulb },
  { id: 'b2b', label: 'Leads B2B', icon: Building2 },
  { id: 'shipping', label: 'Livraison', icon: Truck },
  { id: 'referrals', label: 'Parrainage', icon: Gift },
  { id: 'settings', label: 'Parametres', icon: Settings },
  { id: 'deploy', label: 'Deploiement', icon: Rocket },
  { id: 'translations', label: 'Langues', icon: Globe },
];

// ===================== STAT CARD =====================
const StatCard = ({ label, value, icon: Icon, color, sub }) => (
  <div className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal p-5 flex items-start gap-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className={`w-12 h-12 rounded-xl border-2 border-charcoal flex items-center justify-center ${color}`}>
      <Icon className="w-6 h-6 text-charcoal" />
    </div>
    <div>
      <p className="text-sm text-charcoal/60 font-medium">{label}</p>
      <p className="text-2xl font-heading font-bold text-charcoal">{value}</p>
      {sub && <p className="text-xs text-charcoal/50 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ===================== OVERVIEW TAB =====================
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer,
} from 'recharts';

const CHART_COLORS = ['#FF6B35', '#FFD23F', '#06A77D', '#3D8DFF', '#B23A48', '#8B5CF6', '#EC4899', '#14B8A6'];

const KpiCard = ({ label, value, icon: Icon, sub, accent = 'bg-primary/15', trend }) => (
  <div className="card-brutal p-3 flex items-start gap-2.5">
    <div className={`${accent} w-9 h-9 rounded-lg border border-charcoal/15 flex items-center justify-center shrink-0`}>
      <Icon className="w-4 h-4 text-charcoal" strokeWidth={2.5} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] uppercase font-bold tracking-wider text-charcoal/50 leading-tight">{label}</p>
      <p className="font-heading font-bold text-xl text-charcoal leading-tight">{value}</p>
      {sub && <p className="text-[10px] text-charcoal/40 truncate mt-0.5">{sub}</p>}
      {trend !== undefined && (
        <p className={`text-[10px] font-bold ${trend >= 0 ? 'text-secondary' : 'text-danger'}`}>
          {trend >= 0 ? '▲' : '▼'} {Math.abs(trend)}%
        </p>
      )}
    </div>
  </div>
);

// Fill missing days with 0 over a 30-day window
const fillSeries = (series, dateKey = 'date', valueKeys = ['count']) => {
  const map = new Map((series || []).map((d) => [d[dateKey], d]));
  const out = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const dt = new Date(today);
    dt.setDate(today.getDate() - i);
    const key = dt.toISOString().substring(0, 10);
    const found = map.get(key) || {};
    const entry = { date: key.substring(5) };
    valueKeys.forEach((vk) => { entry[vk] = found[vk] || 0; });
    out.push(entry);
  }
  return out;
};

const OverviewTab = ({ stats }) => {
  if (!stats) return <div className="text-center py-12 text-charcoal/50">Chargement...</div>;

  const ordersSeries = fillSeries(stats.orders_by_day, 'date', ['count', 'revenue']);
  const usersSeries = fillSeries(stats.users_by_day, 'date', ['count']);
  const pingmesSeries = fillSeries(stats.pingmes_by_day, 'date', ['count', 'admin', 'user']);
  const scansSeries = fillSeries(stats.scans_by_day, 'date', ['count']);
  const convsSeries = fillSeries(stats.conversations_by_day, 'date', ['count']);

  const categoryData = (stats.pingmes_by_category || []).slice(0, 8).map((c) => ({
    name: categoryLabel(c.category) || c.category,
    value: c.count,
  }));

  const authData = (stats.users_by_auth || []).map((a) => ({
    name: a.provider === 'google' ? 'Google' : a.provider === 'email' ? 'Email' : a.provider,
    value: a.count,
  }));

  const lostRatio = stats.total_pingmes > 0 ? Math.round((stats.lost_pingmes / stats.total_pingmes) * 100) : 0;
  const archivedRatio = stats.total_pingmes > 0 ? Math.round((stats.archived_pingmes / stats.total_pingmes) * 100) : 0;
  const unassignedRatio = stats.total_pingmes > 0 ? Math.round((stats.unassigned_pingmes / stats.total_pingmes) * 100) : 0;
  const scansPerPingme = stats.total_pingmes ? (stats.total_scans / stats.total_pingmes).toFixed(1) : '0';

  const referrals = stats.referrals || {};

  const tooltipStyle = {
    backgroundColor: '#fff',
    border: '2px solid #1a1a1a',
    borderRadius: 8,
    fontSize: 12,
    fontFamily: 'Inter, system-ui, sans-serif',
  };

  return (
    <div data-testid="admin-overview-tab" className="space-y-6">
      {/* === KPIs (compact) === */}
      <div>
        <h3 className="text-[11px] font-bold tracking-[0.2em] uppercase text-charcoal/50 mb-2">Indicateurs cles</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
          <KpiCard label="Utilisateurs" value={stats.total_users} icon={Users} accent="bg-blue-100" sub={`+${stats.new_users_7d || 0} cette sem.`} />
          <KpiCard label="PingMes" value={stats.total_pingmes} icon={QrCode} accent="bg-primary/20" sub={`${stats.active_pingmes} actifs`} />
          <KpiCard label="Scans" value={stats.total_scans} icon={ScanLine} accent="bg-purple-100" sub={`${scansPerPingme} / pingme`} />
          <KpiCard label="Convers." value={stats.total_conversations} icon={MessageCircle} accent="bg-pink-100" sub={`${stats.scan_to_conversation_pct || 0}% taux`} />
          <KpiCard label="Commandes" value={stats.total_orders} icon={ShoppingCart} accent="bg-secondary/40" sub={`${stats.orders_7d || 0} cette sem.`} />
          <KpiCard label="Revenu" value={`${stats.total_revenue.toFixed(0)}€`} icon={DollarSign} accent="bg-accent/40" sub={`AOV ${stats.aov || 0}€`} />
          <KpiCard label="Mode Perdu" value={stats.lost_pingmes} icon={Eye} accent="bg-red-100" sub={`${lostRatio}% du parc`} />
          <KpiCard label="Archives" value={stats.archived_pingmes || 0} icon={Archive} accent="bg-gray-100" sub={`${archivedRatio}%`} />
          <KpiCard label="Non assignes" value={stats.unassigned_pingmes || 0} icon={QrCode} accent="bg-yellow-100" sub={`${unassignedRatio}%`} />
          <KpiCard label="Organisations" value={stats.total_organizations || 0} icon={Building2} accent="bg-blue-50" sub="Comptes B2B" />
          <KpiCard label="Codes promo" value={referrals.active_codes || 0} icon={Gift} accent="bg-green-100" sub={`${referrals.total_uses || 0} utilisations`} />
          <KpiCard label="A payer" value={`${(referrals.pending_rewards || 0).toFixed(0)}€`} icon={DollarSign} accent="bg-orange-100" sub="Commissions parrains" />
        </div>
      </div>

      {/* === Acquisition row : Users + PingMes + Scans === */}
      <div>
        <h3 className="text-[11px] font-bold tracking-[0.2em] uppercase text-charcoal/50 mb-2">Acquisition (30 jours)</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Users acquisition */}
          <div className="card-brutal p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-heading font-bold text-sm">Nouveaux utilisateurs</h4>
              <span className="text-[10px] font-bold text-charcoal/50">{stats.new_users_30d || 0} sur 30j</span>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={usersSeries}>
                <defs>
                  <linearGradient id="gUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3D8DFF" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#3D8DFF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a10" />
                <XAxis dataKey="date" tick={{ fontSize: 9 }} interval={4} />
                <YAxis tick={{ fontSize: 9 }} width={28} allowDecimals={false} />
                <RTooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="count" stroke="#3D8DFF" fill="url(#gUsers)" strokeWidth={2} name="Inscriptions" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* PingMes created (split admin vs user) */}
          <div className="card-brutal p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-heading font-bold text-sm">PingMes generes</h4>
              <span className="text-[10px] font-bold text-charcoal/50">Admin vs Users</span>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={pingmesSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a10" />
                <XAxis dataKey="date" tick={{ fontSize: 9 }} interval={4} />
                <YAxis tick={{ fontSize: 9 }} width={28} allowDecimals={false} />
                <RTooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="user" stackId="a" fill="#FF6B35" name="Par utilisateurs" />
                <Bar dataKey="admin" stackId="a" fill="#1a1a1a" name="Genere admin" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Scans + Conversations */}
          <div className="card-brutal p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-heading font-bold text-sm">Scans &amp; conversations</h4>
              <span className="text-[10px] font-bold text-charcoal/50">Engagement</span>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={scansSeries.map((s, i) => ({ ...s, conv: convsSeries[i]?.count || 0 }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a10" />
                <XAxis dataKey="date" tick={{ fontSize: 9 }} interval={4} />
                <YAxis tick={{ fontSize: 9 }} width={28} allowDecimals={false} />
                <RTooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Line type="monotone" dataKey="count" stroke="#8B5CF6" strokeWidth={2} dot={false} name="Scans" />
                <Line type="monotone" dataKey="conv" stroke="#EC4899" strokeWidth={2} dot={false} name="Conversations" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* === Revenue + breakdowns === */}
      <div>
        <h3 className="text-[11px] font-bold tracking-[0.2em] uppercase text-charcoal/50 mb-2">Business</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Revenue 30d */}
          <div className="card-brutal p-4 lg:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-heading font-bold text-sm">Chiffre d'affaires (30j)</h4>
              <span className="text-[10px] font-bold text-charcoal/50">{(stats.revenue_30d || 0).toFixed(2)}€ · {stats.orders_7d || 0} cmd /sem</span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={ordersSeries}>
                <defs>
                  <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06A77D" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#06A77D" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a10" />
                <XAxis dataKey="date" tick={{ fontSize: 9 }} interval={3} />
                <YAxis yAxisId="rev" tick={{ fontSize: 9 }} width={36} />
                <YAxis yAxisId="cmd" orientation="right" tick={{ fontSize: 9 }} width={24} allowDecimals={false} />
                <RTooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Area yAxisId="rev" type="monotone" dataKey="revenue" stroke="#06A77D" fill="url(#gRev)" strokeWidth={2} name="Revenu (€)" />
                <Bar yAxisId="cmd" dataKey="count" fill="#FFD23F" stroke="#1a1a1a" name="Commandes" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Auth providers */}
          <div className="card-brutal p-4">
            <h4 className="font-heading font-bold text-sm mb-2">Authentification</h4>
            {authData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={authData} cx="50%" cy="50%" outerRadius={70} innerRadius={36} dataKey="value" label={(d) => `${d.name} (${d.value})`} labelLine={false} fontSize={10}>
                    {authData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="#1a1a1a" strokeWidth={1.5} />
                    ))}
                  </Pie>
                  <RTooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-charcoal/40 text-center py-8 text-sm">Aucune donnee</p>
            )}
          </div>
        </div>
      </div>

      {/* === Catalog + Top PingMes === */}
      <div>
        <h3 className="text-[11px] font-bold tracking-[0.2em] uppercase text-charcoal/50 mb-2">Catalogue &amp; engagement</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Categories */}
          <div className="card-brutal p-4">
            <h4 className="font-heading font-bold text-sm mb-3">PingMes par categorie</h4>
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={categoryData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a10" />
                  <XAxis type="number" tick={{ fontSize: 9 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={120} />
                  <RTooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="value" fill="#FF6B35" stroke="#1a1a1a" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-charcoal/40 text-center py-8 text-sm">Aucun PingMe</p>
            )}
          </div>

          {/* Top scanned PingMes */}
          <div className="card-brutal p-4">
            <h4 className="font-heading font-bold text-sm mb-3">Top 10 des PingMes les plus scannes</h4>
            {(stats.top_pingmes || []).length > 0 ? (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {stats.top_pingmes.map((p, i) => {
                  const max = Math.max(...stats.top_pingmes.map((x) => x.scan_count));
                  return (
                    <div key={p.pingme_id} className="flex items-center gap-2.5">
                      <span className="text-[11px] font-bold text-charcoal/40 w-5 text-right">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <p className="text-xs font-bold truncate">{p.name}</p>
                          <span className="text-xs font-bold shrink-0">{p.scan_count} scans</span>
                        </div>
                        <div className="bg-gray-100 rounded-full h-2 border border-charcoal/10 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${p.is_lost ? 'bg-danger' : 'bg-secondary'}`}
                            style={{ width: `${Math.max((p.scan_count / max) * 100, 4)}%` }}
                          />
                        </div>
                        <p className="text-[10px] text-charcoal/40">{categoryLabel(p.category) || p.category}{p.is_lost ? ' · PERDU' : ''}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-charcoal/40 text-center py-8 text-sm">Aucun PingMe scanne</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ===================== USERS TAB =====================
const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const limit = 15;

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users?skip=${page * limit}&limit=${limit}&search=${search}`);
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch { toast.error('Erreur chargement utilisateurs'); }
  }, [page, search]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const changeRole = async (userId, newRole) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/role`, { role: newRole });
      toast.success(`Role mis a jour: ${newRole}`);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div data-testid="admin-users-tab">
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal/40" />
          <input
            data-testid="users-search-input"
            className="input-brutal w-full pl-10"
            placeholder="Rechercher par nom ou email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          />
        </div>
        <span className="text-sm text-charcoal/50">{total} utilisateur(s)</span>
      </div>
      <div className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-charcoal text-white">
              <tr>
                <th className="px-4 py-3 text-left font-heading">Nom</th>
                <th className="px-4 py-3 text-left font-heading">Email</th>
                <th className="px-4 py-3 text-left font-heading">Role</th>
                <th className="px-4 py-3 text-left font-heading">Auth</th>
                <th className="px-4 py-3 text-center font-heading">PingMes</th>
                <th className="px-4 py-3 text-center font-heading">Commandes</th>
                <th className="px-4 py-3 text-left font-heading">Inscription</th>
                <th className="px-4 py-3 text-center font-heading">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-t border-charcoal/10 hover:bg-surface-alt/50" data-testid={`user-row-${u.user_id}`}>
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 text-charcoal/70">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold px-2 py-1 rounded-full border ${
                      u.role === 'admin' ? 'bg-accent border-charcoal' :
                      u.role === 'partner' ? 'bg-primary/30 border-charcoal text-charcoal' :
                      'bg-gray-100 border-gray-300'
                    }`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-charcoal/60 text-xs">{u.auth_provider || 'email'}</td>
                  <td className="px-4 py-3 text-center">{u.pingme_count}</td>
                  <td className="px-4 py-3 text-center">{u.order_count}</td>
                  <td className="px-4 py-3 text-charcoal/60 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '-'}</td>
                  <td className="px-4 py-3 text-center">
                    <select
                      className="text-xs border-2 border-charcoal/20 rounded-lg px-2 py-1 bg-white font-bold cursor-pointer hover:border-charcoal/40 transition-colors"
                      value={u.role}
                      onChange={(e) => changeRole(u.user_id, e.target.value)}
                      data-testid={`role-select-${u.user_id}`}
                    >
                      <option value="user">Membre</option>
                      <option value="partner">Partenaire</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-charcoal/40">Aucun utilisateur</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination page={page} setPage={setPage} total={total} limit={limit} />
    </div>
  );
};

// ===================== ORDERS TAB =====================
const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  confirmed: 'bg-blue-100 text-blue-800 border-blue-300',
  shipped: 'bg-purple-100 text-purple-800 border-purple-300',
  delivered: 'bg-green-100 text-green-800 border-green-300',
  cancelled: 'bg-red-100 text-red-800 border-red-300',
};
const statusLabels = { pending: 'En attente', confirmed: 'Confirmee', shipped: 'Expediee', delivered: 'Livree', cancelled: 'Annulee' };

const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [editingOrder, setEditingOrder] = useState(null);
  const limit = 15;

  const fetchOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/orders?skip=${page * limit}&limit=${limit}&status=${statusFilter}`);
      setOrders(res.data.orders);
      setTotal(res.data.total);
    } catch { toast.error('Erreur chargement commandes'); }
  }, [page, statusFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const updateStatus = async (orderId, status) => {
    try {
      await axios.patch(`${API}/admin/orders/${orderId}`, { status });
      toast.success('Statut mis a jour');
      setEditingOrder(null);
      fetchOrders();
    } catch { toast.error('Erreur mise a jour'); }
  };

  return (
    <div data-testid="admin-orders-tab">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <select
          data-testid="orders-status-filter"
          className="input-brutal text-sm"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
        >
          <option value="">Tous les statuts</option>
          {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <span className="text-sm text-charcoal/50">{total} commande(s)</span>
      </div>
      <div className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-charcoal text-white">
              <tr>
                <th className="px-4 py-3 text-left font-heading">ID</th>
                <th className="px-4 py-3 text-left font-heading">Client</th>
                <th className="px-4 py-3 text-center font-heading">Articles</th>
                <th className="px-4 py-3 text-right font-heading">Total</th>
                <th className="px-4 py-3 text-center font-heading">Statut</th>
                <th className="px-4 py-3 text-left font-heading">Date</th>
                <th className="px-4 py-3 text-center font-heading">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.order_id} className="border-t border-charcoal/10 hover:bg-surface-alt/50" data-testid={`order-row-${o.order_id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{o.order_id}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{o.user_name}</div>
                    <div className="text-xs text-charcoal/50">{o.user_email}</div>
                  </td>
                  <td className="px-4 py-3 text-center">{o.items?.length || 0}</td>
                  <td className="px-4 py-3 text-right font-bold">{(o.total || 0).toFixed(2)} EUR</td>
                  <td className="px-4 py-3 text-center">
                    {editingOrder === o.order_id ? (
                      <div className="flex items-center gap-1 justify-center">
                        {Object.entries(statusLabels).map(([k, v]) => (
                          <button key={k} onClick={() => updateStatus(o.order_id, k)} className={`text-xs px-2 py-1 rounded-full border ${statusColors[k]} hover:opacity-80`}>{v}</button>
                        ))}
                        <button onClick={() => setEditingOrder(null)} className="p-1"><X className="w-3 h-3" /></button>
                      </div>
                    ) : (
                      <span className={`text-xs font-bold px-2 py-1 rounded-full border ${statusColors[o.status] || 'bg-gray-100'}`}>
                        {statusLabels[o.status] || o.status}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-charcoal/60 text-xs">{o.created_at ? new Date(o.created_at).toLocaleDateString('fr-FR') : '-'}</td>
                  <td className="px-4 py-3 text-center">
                    <button data-testid={`edit-order-${o.order_id}`} onClick={() => setEditingOrder(o.order_id)} className="p-1 hover:bg-surface-alt rounded-lg"><Pencil className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-charcoal/40">Aucune commande</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination page={page} setPage={setPage} total={total} limit={limit} />
    </div>
  );
};

// ===================== PRODUCTS TAB =====================
const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', price: '', image_url: '', category: 'stickers', stock: '100' });

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
    } catch { toast.error('Erreur chargement produits'); }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleSave = async () => {
    try {
      if (editingProduct) {
        await axios.patch(`${API}/admin/products/${editingProduct}`, form);
        toast.success('Produit mis a jour');
      } else {
        await axios.post(`${API}/admin/products`, form);
        toast.success('Produit cree');
      }
      setEditingProduct(null);
      setShowCreate(false);
      setForm({ name: '', description: '', price: '', image_url: '', category: 'stickers', stock: '100' });
      fetchProducts();
    } catch { toast.error('Erreur sauvegarde'); }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success('Produit supprime');
      fetchProducts();
    } catch { toast.error('Erreur suppression'); }
  };

  const startEdit = (p) => {
    setEditingProduct(p.product_id);
    setForm({ name: p.name, description: p.description, price: String(p.price), image_url: p.image_url, category: p.category, stock: String(p.stock) });
    setShowCreate(true);
  };

  return (
    <div data-testid="admin-products-tab">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-charcoal/50">{products.length} produit(s)</span>
        <button
          data-testid="add-product-btn"
          onClick={() => { setEditingProduct(null); setForm({ name: '', description: '', price: '', image_url: '', category: 'stickers', stock: '100' }); setShowCreate(true); }}
          className="btn-primary text-sm px-4 py-2"
        >
          + Ajouter
        </button>
      </div>

      {showCreate && (
        <div className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal p-6 mb-6" data-testid="product-form">
          <h3 className="font-heading font-bold mb-4">{editingProduct ? 'Modifier le produit' : 'Nouveau produit'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input className="input-brutal" placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" />
            <input className="input-brutal" placeholder="Prix" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price-input" />
            <input className="input-brutal" placeholder="URL Image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
            <input className="input-brutal" placeholder="Stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
            <select className="input-brutal" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              <option value="stickers">Stickers</option>
              <option value="keychains">Porte-cles</option>
              <option value="pet_tags">Medailles</option>
              <option value="bracelets">Bracelets</option>
              <option value="car_stickers">Auto</option>
              <option value="luggage">Bagages</option>
              <option value="doudou">Doudou</option>
            </select>
            <textarea className="input-brutal md:col-span-2" placeholder="Description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={handleSave} className="btn-primary text-sm px-6 py-2" data-testid="product-save-btn">Enregistrer</button>
            <button onClick={() => { setShowCreate(false); setEditingProduct(null); }} className="btn-outline text-sm px-6 py-2">Annuler</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.product_id} className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal p-4 flex flex-col" data-testid={`product-card-${p.product_id}`}>
            {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-32 object-cover rounded-lg border border-charcoal/10 mb-3" />}
            <h4 className="font-heading font-bold text-sm">{p.name}</h4>
            <p className="text-xs text-charcoal/60 mt-1 flex-1">{p.description?.slice(0, 80)}...</p>
            <div className="flex items-center justify-between mt-3">
              <span className="font-bold text-primary">{p.price} EUR</span>
              <span className="text-xs text-charcoal/50">Stock: {p.stock}</span>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => startEdit(p)} className="flex-1 btn-outline text-xs py-1.5 flex items-center justify-center gap-1"><Pencil className="w-3 h-3" /> Modifier</button>
              <button onClick={() => handleDelete(p.product_id)} className="btn-outline text-xs py-1.5 px-3 text-danger border-danger hover:bg-red-50"><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ===================== PINGMES TAB =====================
const PingMesTab = () => {
  const [pingmes, setPingmes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [catFilter, setCatFilter] = useState('');
  const limit = 15;

  const fetchPingmes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/pingmes?skip=${page * limit}&limit=${limit}&category=${catFilter}`);
      setPingmes(res.data.pingmes);
      setTotal(res.data.total);
    } catch { toast.error('Erreur chargement PingMes'); }
  }, [page, catFilter]);

  useEffect(() => { fetchPingmes(); }, [fetchPingmes]);

  return (
    <div data-testid="admin-pingmes-tab">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <select
          data-testid="pingmes-category-filter"
          className="input-brutal text-sm"
          value={catFilter}
          onChange={(e) => { setCatFilter(e.target.value); setPage(0); }}
        >
          <option value="">Toutes les categories</option>
          {Object.entries(allCategories).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <span className="text-sm text-charcoal/50">{total} PingMe(s)</span>
      </div>
      <div className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-charcoal text-white">
              <tr>
                <th className="px-4 py-3 text-left font-heading">Nom</th>
                <th className="px-4 py-3 text-left font-heading">Categorie</th>
                <th className="px-4 py-3 text-left font-heading">Proprietaire</th>
                <th className="px-4 py-3 text-center font-heading">Actif</th>
                <th className="px-4 py-3 text-center font-heading">Perdu</th>
                <th className="px-4 py-3 text-center font-heading">Scans</th>
                <th className="px-4 py-3 text-left font-heading">Cree le</th>
                <th className="px-4 py-3 text-center font-heading">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pingmes.map((p) => (
                <tr key={p.pingme_id} className={`border-t border-charcoal/10 hover:bg-surface-alt/50 ${!p.user_id ? 'bg-yellow-50/50' : ''}`} data-testid={`pingme-row-${p.pingme_id}`}>
                  <td className="px-4 py-3 font-medium">{p.name}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs bg-surface-alt px-2 py-1 rounded-full border border-charcoal/10">{categoryLabel(p.category) || p.category}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs">{p.user_name}</div>
                    <div className="text-xs text-charcoal/50">{p.user_email}</div>
                  </td>
                  <td className="px-4 py-3 text-center">{p.is_active ? <Check className="w-4 h-4 text-green-600 mx-auto" /> : <X className="w-4 h-4 text-red-500 mx-auto" />}</td>
                  <td className="px-4 py-3 text-center">{p.is_lost ? <Check className="w-4 h-4 text-red-600 mx-auto" /> : <span className="text-charcoal/30">-</span>}</td>
                  <td className="px-4 py-3 text-center font-bold">{p.scan_count || 0}</td>
                  <td className="px-4 py-3 text-charcoal/60 text-xs">{p.created_at ? new Date(p.created_at).toLocaleDateString('fr-FR') : '-'}</td>
                  <td className="px-4 py-3 text-center">
                    {!p.user_id && (
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Supprimer ${p.name} ?`)) return;
                          try {
                            await axios.delete(`${API}/admin/pingmes/${p.pingme_id}`);
                            toast.success('PingMe supprime');
                            fetchPingmes();
                          } catch { toast.error('Erreur'); }
                        }}
                        className="text-danger hover:text-danger/80 p-1"
                        title="Supprimer (non assigne)"
                        data-testid={`delete-pingme-${p.pingme_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {pingmes.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-charcoal/40">Aucun PingMe</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Bulk delete unassigned */}
      {pingmes.some(p => !p.user_id) && (
        <div className="mt-4 flex justify-end">
          <button
            onClick={async () => {
              const count = pingmes.filter(p => !p.user_id).length;
              if (!window.confirm(`Supprimer tous les PingMes non assignes (${count} visible(s)) ?`)) return;
              try {
                const res = await axios.delete(`${API}/admin/pingmes/unassigned`);
                toast.success(`${res.data.deleted} PingMe(s) non assigne(s) supprime(s)`);
                fetchPingmes();
              } catch { toast.error('Erreur'); }
            }}
            className="text-sm px-4 py-2 rounded-full border-2 border-danger text-danger font-bold hover:bg-danger hover:text-white transition-all"
            data-testid="delete-all-unassigned-btn"
          >
            <span className="flex items-center gap-2"><Trash2 className="w-4 h-4" /> Supprimer tous les non assignes</span>
          </button>
        </div>
      )}
      <Pagination page={page} setPage={setPage} total={total} limit={limit} />
    </div>
  );
};

// ===================== GENERATE PLATES TAB =====================
const SHEET_TEMPLATES = [
  { id: 'classic', label: 'Classique' },
  { id: 'round', label: 'Rond' },
  { id: 'tag', label: 'Etiquette' },
  { id: 'minimal', label: 'Minimal' },
  { id: 'card', label: 'Carte' },
];
const PASTEL_PRESETS = [
  '#FCE4EC', '#FFE0B2', '#FFE082', '#D0E6C2', '#B3E5FC', '#D1C4E9',
  '#F8BBD0', '#FFAB91', '#C5E1A5', '#80DEEA', '#90CAF9', '#B39DDB',
  '#212121', '#FFFFFF',
];

const GenerateTab = () => {
  const [labelText, setLabelText] = useState('perdu ? Scannez moi');
  const [template, setTemplate] = useState('classic');
  const [colors, setColors] = useState(PASTEL_PRESETS.slice(0, 6));
  const [generating, setGenerating] = useState(false);
  const [sheet, setSheet] = useState(null); // { image_base64, count, persisted }

  const toggleColor = (hex) => {
    setColors((prev) => {
      if (prev.includes(hex)) return prev.filter((c) => c !== hex);
      if (prev.length >= 6) return [...prev.slice(1), hex];
      return [...prev, hex];
    });
  };

  const generateSheet = async () => {
    if (colors.length === 0) {
      toast.error('Choisissez au moins une couleur');
      return;
    }
    setGenerating(true);
    setSheet(null);
    try {
      const res = await axios.post(`${API}/admin/generate-sticker-sheet`, {
        template,
        colors,
        label_text: labelText,
        persist: true,
      });
      setSheet(res.data);
      toast.success(`Feuille A5 generee : ${res.data.stickers_count} etiquettes`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur generation');
    } finally { setGenerating(false); }
  };

  const downloadSheet = () => {
    if (!sheet) return;
    const link = document.createElement('a');
    link.download = `feuille-a5-${template}-${new Date().getTime()}.png`;
    link.href = `data:image/png;base64,${sheet.image_base64}`;
    link.click();
  };

  const printSheet = () => {
    if (!sheet) return;
    const w = window.open('', '_blank');
    w.document.write(`
      <html><head><title>Feuille PingMe A5</title>
      <style>
        @page { size: A5; margin: 0; }
        body { margin: 0; padding: 0; }
        img { width: 148mm; height: 210mm; display: block; }
      </style>
      </head><body>
        <img src="data:image/png;base64,${sheet.image_base64}" />
      </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  return (
    <div data-testid="admin-generate-tab">
      <div className="card-brutal mb-6">
        <h3 className="font-heading font-bold text-lg mb-1 flex items-center gap-2">
          <Printer className="w-5 h-5" /> Generer une feuille A5 de plaquettes
        </h3>
        <p className="text-sm text-charcoal/60 mb-5">
          Genere une feuille A5 prete a imprimer avec des etiquettes de differentes tailles, dans le style choisi et les couleurs selectionnees. Chaque etiquette a son propre QR unique et est enregistree comme PingMe non assigne.
        </p>

        {/* Template selector */}
        <div className="mb-4">
          <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">Style d'etiquette</label>
          <div className="flex flex-wrap gap-2">
            {SHEET_TEMPLATES.map((t) => (
              <button
                key={t.id}
                onClick={() => setTemplate(t.id)}
                data-testid={`template-${t.id}`}
                className={`px-3 py-1.5 rounded-full border-2 text-xs font-bold transition-all ${
                  template === t.id ? 'border-charcoal bg-charcoal text-white' : 'border-charcoal/20 text-charcoal/70 hover:border-charcoal/50'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Color palette - max 6 */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal">Couleurs ({colors.length}/6)</label>
            <button onClick={() => setColors(PASTEL_PRESETS.slice(0, 6))} className="text-[11px] font-bold text-charcoal/50 hover:text-charcoal">Reinitialiser</button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {PASTEL_PRESETS.map((c) => {
              const selected = colors.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleColor(c)}
                  data-testid={`sheet-color-${c.replace('#', '')}`}
                  className={`relative w-9 h-9 rounded-lg border-2 transition-all hover:scale-110 ${
                    selected ? 'border-charcoal scale-110 shadow-brutal-sm' : 'border-charcoal/15'
                  }`}
                  style={{ backgroundColor: c }}
                  title={c}
                >
                  {selected && (
                    <span className="absolute -top-1.5 -right-1.5 bg-charcoal text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{colors.indexOf(c) + 1}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Label text */}
        <div className="mb-4">
          <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Texte de l'etiquette</label>
          <input
            className="input-brutal w-full"
            value={labelText}
            onChange={(e) => setLabelText(e.target.value)}
            placeholder="perdu ? Scannez moi"
            data-testid="generate-label-text"
          />
        </div>

        <button
          onClick={generateSheet}
          disabled={generating || colors.length === 0}
          className="btn-primary flex items-center gap-2 disabled:opacity-50"
          data-testid="generate-sheet-btn"
        >
          <QrCode className="w-5 h-5" />
          {generating ? 'Generation...' : 'Generer la feuille A5'}
        </button>
      </div>

      {sheet && (
        <div className="card-brutal" data-testid="generated-sheet">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <h4 className="font-heading font-bold text-lg">Feuille A5 prete</h4>
              <p className="text-xs text-charcoal/60">{sheet.stickers_count} etiquettes uniques · style {template}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={downloadSheet} className="btn-outline text-sm flex items-center gap-2" data-testid="sheet-download-btn">
                <Download className="w-4 h-4" /> Telecharger PNG
              </button>
              <button onClick={printSheet} className="btn-primary text-sm flex items-center gap-2" data-testid="sheet-print-btn">
                <Printer className="w-4 h-4" /> Imprimer
              </button>
            </div>
          </div>
          <div className="bg-surface-alt rounded-xl p-4 flex justify-center">
            <img
              src={`data:image/png;base64,${sheet.image_base64}`}
              alt="A5 sheet"
              className="max-w-full h-auto border-2 border-charcoal/10 rounded-lg shadow-brutal-sm"
              style={{ maxHeight: '900px' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ===================== SUGGESTIONS TAB =====================
const suggestionStatusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  reviewed: 'bg-blue-100 text-blue-800 border-blue-300',
  accepted: 'bg-green-100 text-green-800 border-green-300',
  rejected: 'bg-red-100 text-red-800 border-red-300',
  in_progress: 'bg-purple-100 text-purple-800 border-purple-300'
};
const suggestionStatusLabels = {
  pending: 'En attente', reviewed: 'En revue', accepted: 'Approuvee', rejected: 'Rejetee', in_progress: 'En cours'
};

const SuggestionsTab = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const fetchSuggestions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/suggestions?archived=${showArchived}`);
      setSuggestions(res.data.suggestions);
      setTotal(res.data.total);
    } catch { toast.error('Erreur chargement suggestions'); }
  }, [showArchived]);

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/suggestions/${id}`, { status });
      toast.success('Statut mis a jour');
      fetchSuggestions();
    } catch { toast.error('Erreur'); }
  };

  const sendResponse = async (id) => {
    if (!responseText.trim()) return;
    try {
      await axios.patch(`${API}/admin/suggestions/${id}`, { admin_response: responseText });
      toast.success('Reponse envoyee');
      setRespondingTo(null);
      setResponseText('');
      fetchSuggestions();
    } catch { toast.error('Erreur'); }
  };

  const toggleArchive = async (id, archived) => {
    try {
      await axios.patch(`${API}/admin/suggestions/${id}`, { archived });
      toast.success(archived ? 'Suggestion archivee' : 'Suggestion desarchivee');
      fetchSuggestions();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div data-testid="admin-suggestions-tab">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-charcoal/50">{total} suggestion(s) {showArchived ? 'archivee(s)' : 'active(s)'}</p>
        <button
          onClick={() => setShowArchived(!showArchived)}
          className={`text-sm px-4 py-2 rounded-full border-2 font-bold transition-all ${showArchived ? 'bg-charcoal text-white border-charcoal' : 'bg-white text-charcoal border-charcoal/20 hover:border-charcoal/40'}`}
          data-testid="toggle-archived-suggestions"
        >
          {showArchived ? 'Voir actives' : 'Voir archives'}
        </button>
      </div>
      <div className="space-y-3">
        {suggestions.map((s) => (
          <div key={s.suggestion_id} className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal p-5" data-testid={`suggestion-${s.suggestion_id}`}>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex-1">
                <p className="font-medium">{s.suggestion}</p>
                {s.email && <p className="text-xs text-charcoal/50 mt-1">De: {s.email}</p>}
                <p className="text-xs text-charcoal/40 mt-1">{s.created_at ? new Date(s.created_at).toLocaleDateString('fr-FR') : ''}</p>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full border font-bold shrink-0 ${suggestionStatusColors[s.status] || 'bg-gray-100 border-gray-300'}`}>
                {suggestionStatusLabels[s.status] || s.status}
              </span>
            </div>

            {/* Status actions */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {['pending', 'in_progress', 'accepted', 'rejected'].map((st) => (
                <button
                  key={st}
                  onClick={() => updateStatus(s.suggestion_id, st)}
                  disabled={s.status === st}
                  className={`text-xs px-3 py-1.5 rounded-full border-2 font-bold transition-all ${s.status === st ? 'opacity-50 cursor-not-allowed border-charcoal/20' : 'border-charcoal/30 hover:border-charcoal hover:shadow-brutal-sm cursor-pointer'}`}
                  data-testid={`suggestion-status-${st}-${s.suggestion_id}`}
                >
                  {suggestionStatusLabels[st]}
                </button>
              ))}
              <button
                onClick={() => { setRespondingTo(respondingTo === s.suggestion_id ? null : s.suggestion_id); setResponseText(s.admin_response || ''); }}
                className="text-xs px-3 py-1.5 rounded-full border-2 border-primary bg-primary/10 font-bold hover:shadow-brutal-sm transition-all"
                data-testid={`suggestion-respond-${s.suggestion_id}`}
              >
                {respondingTo === s.suggestion_id ? 'Fermer' : (s.admin_response ? 'Modifier reponse' : 'Repondre')}
              </button>
              <button
                onClick={() => toggleArchive(s.suggestion_id, !s.archived)}
                className="text-xs px-3 py-1.5 rounded-full border-2 border-charcoal/20 font-bold hover:border-charcoal/40 hover:shadow-brutal-sm transition-all ml-auto"
                data-testid={`suggestion-archive-${s.suggestion_id}`}
              >
                {s.archived ? 'Desarchiver' : 'Archiver'}
              </button>
            </div>

            {/* Admin response display */}
            {s.admin_response && respondingTo !== s.suggestion_id && (
              <div className="bg-accent/10 border-2 border-accent/30 rounded-xl p-3 mt-2">
                <p className="text-xs font-bold text-charcoal/60 mb-1">Reponse admin :</p>
                <p className="text-sm text-charcoal">{s.admin_response}</p>
              </div>
            )}

            {/* Response form */}
            {respondingTo === s.suggestion_id && (
              <div className="mt-3 space-y-2" data-testid={`suggestion-response-form-${s.suggestion_id}`}>
                <textarea
                  className="input-brutal w-full h-20 resize-none text-sm"
                  placeholder="Votre reponse a cette suggestion..."
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  data-testid={`suggestion-response-input-${s.suggestion_id}`}
                />
                <button
                  onClick={() => sendResponse(s.suggestion_id)}
                  disabled={!responseText.trim()}
                  className="btn-primary text-xs px-4 py-2 disabled:opacity-50"
                  data-testid={`suggestion-response-submit-${s.suggestion_id}`}
                >
                  Envoyer la reponse
                </button>
              </div>
            )}
          </div>
        ))}
        {suggestions.length === 0 && <p className="text-center text-charcoal/40 py-8">Aucune suggestion</p>}
      </div>
    </div>
  );
};

// ===================== SHIPPING TAB =====================
const ShippingTab = () => {
  const [config, setConfig] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/shipping`);
      setConfig(res.data);
      setForm(res.data);
    } catch { toast.error('Erreur chargement config livraison'); }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    try {
      await axios.put(`${API}/admin/shipping`, form);
      toast.success('Configuration sauvegardee');
      setEditing(false);
      fetchConfig();
    } catch { toast.error('Erreur sauvegarde'); }
  };

  if (!config) return <div className="text-center py-12 text-charcoal/50">Chargement...</div>;

  const fields = [
    { key: 'paris_region', label: 'Region parisienne (75, 92, 93, 94)', unit: 'EUR' },
    { key: 'france', label: 'France metropolitaine', unit: 'EUR' },
    { key: 'international', label: 'International', unit: 'EUR' },
    { key: 'free_threshold', label: 'Seuil livraison gratuite', unit: 'EUR' },
  ];

  return (
    <div data-testid="admin-shipping-tab">
      <div className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal p-6 max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading font-bold text-lg">Frais de livraison</h3>
          {!editing && <button onClick={() => setEditing(true)} className="btn-outline text-sm py-1.5 px-4 flex items-center gap-1" data-testid="edit-shipping-btn"><Pencil className="w-3 h-3" /> Modifier</button>}
        </div>
        <div className="space-y-4">
          {fields.map((f) => (
            <div key={f.key} className="flex items-center justify-between">
              <label className="text-sm font-medium">{f.label}</label>
              {editing ? (
                <div className="flex items-center gap-2">
                  <input
                    className="input-brutal w-24 text-right text-sm"
                    type="number"
                    step="0.01"
                    value={form[f.key] || ''}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    data-testid={`shipping-${f.key}-input`}
                  />
                  <span className="text-xs text-charcoal/50">{f.unit}</span>
                </div>
              ) : (
                <span className="font-bold">{config[f.key]} {f.unit}</span>
              )}
            </div>
          ))}
        </div>
        {editing && (
          <div className="flex gap-3 mt-6">
            <button onClick={handleSave} className="btn-primary text-sm px-6 py-2" data-testid="save-shipping-btn">Enregistrer</button>
            <button onClick={() => { setEditing(false); setForm(config); }} className="btn-outline text-sm px-6 py-2">Annuler</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ===================== B2B LEADS TAB =====================
const useCaseLabels = { fleet: 'Flotte', school: 'Ecole', hotel: 'Hotel', enterprise: 'Entreprise', other: 'Autre' };
const leadStatusColors = {
  new: 'bg-blue-100 text-blue-800 border-blue-300',
  contacted: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  qualified: 'bg-purple-100 text-purple-800 border-purple-300',
  converted: 'bg-green-100 text-green-800 border-green-300',
  lost: 'bg-red-100 text-red-800 border-red-300',
};

const B2BLeadsTab = () => {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const limit = 15;

  const fetchLeads = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/b2b-leads?skip=${page * limit}&limit=${limit}&archived=${showArchived}`);
      setLeads(res.data.leads);
      setTotal(res.data.total);
    } catch { toast.error('Erreur chargement leads'); }
  }, [page, showArchived]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const updateStatus = async (leadId, status) => {
    try {
      await axios.patch(`${API}/admin/b2b-leads/${leadId}`, { status });
      toast.success('Lead mis a jour');
      fetchLeads();
    } catch { toast.error('Erreur'); }
  };

  const toggleArchive = async (leadId, archived) => {
    try {
      await axios.patch(`${API}/admin/b2b-leads/${leadId}`, { archived });
      toast.success(archived ? 'Lead archive' : 'Lead desarchive');
      fetchLeads();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div data-testid="admin-b2b-tab">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-charcoal/50">{total} lead(s) B2B {showArchived ? 'archive(s)' : 'actif(s)'}</p>
        <button
          onClick={() => { setShowArchived(!showArchived); setPage(0); }}
          className={`text-sm px-4 py-2 rounded-full border-2 font-bold transition-all ${showArchived ? 'bg-charcoal text-white border-charcoal' : 'bg-white text-charcoal border-charcoal/20 hover:border-charcoal/40'}`}
          data-testid="toggle-archived-b2b"
        >
          {showArchived ? 'Voir actifs' : 'Voir archives'}
        </button>
      </div>
      <div className="space-y-3">
        {leads.map((l) => (
          <div key={l.lead_id} className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal p-5" data-testid={`lead-${l.lead_id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-heading font-bold">{l.company_name}</h4>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${leadStatusColors[l.status] || 'bg-gray-100'}`}>{l.status}</span>
                </div>
                <p className="text-sm text-charcoal/70">{l.contact_name} - {l.email}</p>
                {l.phone && <p className="text-xs text-charcoal/50">{l.phone}</p>}
                <div className="flex gap-3 mt-2 text-xs text-charcoal/60">
                  <span>Taille: {l.company_size}</span>
                  <span>Usage: {useCaseLabels[l.use_case] || l.use_case}</span>
                  <span>Plan: {l.plan_interest}</span>
                </div>
                {l.message && <p className="text-sm text-charcoal/60 mt-2 italic">"{l.message}"</p>}
                <p className="text-xs text-charcoal/40 mt-1">{l.created_at ? new Date(l.created_at).toLocaleDateString('fr-FR') : ''}</p>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <div className="flex gap-1">
                  {['contacted', 'qualified', 'converted', 'lost'].map((s) => (
                    <button key={s} onClick={() => updateStatus(l.lead_id, s)} className={`text-xs px-2 py-1 rounded-full border ${leadStatusColors[s]} hover:opacity-80`}>{s}</button>
                  ))}
                </div>
                <button
                  onClick={() => toggleArchive(l.lead_id, !l.archived)}
                  className="text-xs px-3 py-1.5 rounded-full border-2 border-charcoal/20 font-bold hover:border-charcoal/40 transition-all self-end"
                  data-testid={`archive-lead-${l.lead_id}`}
                >
                  {l.archived ? 'Desarchiver' : 'Archiver'}
                </button>
              </div>
            </div>
          </div>
        ))}
        {leads.length === 0 && <p className="text-center text-charcoal/40 py-8">Aucun lead B2B</p>}
      </div>
      <Pagination page={page} setPage={setPage} total={total} limit={limit} />
    </div>
  );
};

const iconComponents = {
  Key, Car, Dog, Baby, Smartphone, MapPin, Package, Building2,
  Truck, GraduationCap, Hotel, Users, Shield, Heart, QrCode, Globe
};

const iconOptions = [
  { name: 'Key', label: 'Cles' },
  { name: 'Car', label: 'Voiture' },
  { name: 'Dog', label: 'Animal' },
  { name: 'Baby', label: 'Enfant' },
  { name: 'Smartphone', label: 'Telephone' },
  { name: 'MapPin', label: 'Localisation' },
  { name: 'Package', label: 'Colis' },
  { name: 'Building2', label: 'Entreprise' },
  { name: 'Truck', label: 'Vehicule' },
  { name: 'GraduationCap', label: 'Ecole' },
  { name: 'Hotel', label: 'Hotel' },
  { name: 'Users', label: 'Equipe' },
  { name: 'Shield', label: 'Securite' },
  { name: 'Heart', label: 'Sante' },
  { name: 'QrCode', label: 'QR Code' },
  { name: 'Globe', label: 'International' },
];

// ===================== REFERRALS TAB =====================
const ReferralsTab = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: '', influencer_name: '', influencer_email: '', discount_percent: 15, reward_percent: 10, max_uses: '', valid_until: '', is_active: true, notes: '' });
  const [detail, setDetail] = useState(null);
  const [detailUses, setDetailUses] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchCodes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/referral-codes`);
      setCodes(res.data || []);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const openCreate = () => {
    setEditing(null);
    setForm({ code: '', influencer_name: '', influencer_email: '', discount_percent: 15, reward_percent: 10, max_uses: '', valid_until: '', is_active: true, notes: '' });
    setShowForm(true);
  };
  const openEdit = (c) => {
    setEditing(c.code);
    setForm({
      code: c.code,
      influencer_name: c.influencer_name || '',
      influencer_email: c.influencer_email || '',
      discount_percent: c.discount_percent,
      reward_percent: c.reward_percent,
      max_uses: c.max_uses || '',
      valid_until: c.valid_until ? c.valid_until.substring(0, 10) : '',
      is_active: c.is_active !== false,
      notes: c.notes || '',
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        code: form.code,
        influencer_name: form.influencer_name,
        influencer_email: form.influencer_email || null,
        discount_percent: parseFloat(form.discount_percent),
        reward_percent: parseFloat(form.reward_percent),
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
        valid_until: form.valid_until || null,
        is_active: form.is_active,
        notes: form.notes,
      };
      if (editing) {
        await axios.put(`${API}/admin/referral-codes/${editing}`, payload);
        toast.success('Code mis a jour');
      } else {
        await axios.post(`${API}/admin/referral-codes`, payload);
        toast.success('Code cree');
      }
      setShowForm(false);
      fetchCodes();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const removeCode = async (code) => {
    if (!window.confirm(`Supprimer le code ${code} ?`)) return;
    try {
      await axios.delete(`${API}/admin/referral-codes/${code}`);
      toast.success('Code supprime');
      fetchCodes();
    } catch { toast.error('Erreur'); }
  };

  const openDetail = async (c) => {
    setDetail(c);
    try {
      const res = await axios.get(`${API}/admin/referral-codes/${c.code}/uses`);
      setDetailUses(res.data || []);
    } catch { setDetailUses([]); }
  };

  const markPaid = async (code) => {
    if (!window.confirm(`Marquer toutes les retributions de ${code} comme payees ?`)) return;
    try {
      const res = await axios.post(`${API}/admin/referral-codes/${code}/mark-paid`);
      toast.success(`${res.data.amount.toFixed(2)} EUR marques comme payes (${res.data.count} utilisations)`);
      fetchCodes();
      if (detail) openDetail({ ...detail });
    } catch { toast.error('Erreur'); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Code ${code} copie`);
  };

  if (loading) return <div className="text-center py-12 text-charcoal/50">Chargement...</div>;

  const totalPending = codes.reduce((acc, c) => acc + (c.pending_reward || 0), 0);
  const totalPaid = codes.reduce((acc, c) => acc + (c.paid_reward || 0), 0);
  const totalRevenue = codes.reduce((acc, c) => acc + (c.total_revenue || 0), 0);

  return (
    <div data-testid="admin-referrals-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-xl">Codes de parrainage</h2>
          <p className="text-sm text-charcoal/60">Gerez les codes promo des influenceurs</p>
        </div>
        <button onClick={openCreate} className="btn-primary inline-flex items-center gap-2" data-testid="referral-create-btn">
          <Plus className="w-4 h-4" /> Nouveau code
        </button>
      </div>

      {/* Aggregated stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="card-brutal p-4"><p className="text-xs uppercase font-bold text-charcoal/50">Codes actifs</p><p className="text-2xl font-heading font-bold mt-1">{codes.filter(c => c.is_active !== false).length}</p></div>
        <div className="card-brutal p-4"><p className="text-xs uppercase font-bold text-charcoal/50">Utilisations</p><p className="text-2xl font-heading font-bold mt-1">{codes.reduce((acc, c) => acc + (c.usage_count || 0), 0)}</p></div>
        <div className="card-brutal p-4 bg-accent/10"><p className="text-xs uppercase font-bold text-charcoal/50">CA genere</p><p className="text-2xl font-heading font-bold mt-1">{totalRevenue.toFixed(2)} EUR</p></div>
        <div className="card-brutal p-4 bg-primary/10"><p className="text-xs uppercase font-bold text-charcoal/50">A payer</p><p className="text-2xl font-heading font-bold mt-1 text-danger">{totalPending.toFixed(2)} EUR</p><p className="text-[10px] text-charcoal/40 mt-1">Paye: {totalPaid.toFixed(2)} EUR</p></div>
      </div>

      {/* Codes table */}
      {codes.length === 0 ? (
        <div className="card-brutal text-center py-10">
          <Gift className="w-12 h-12 text-charcoal/20 mx-auto mb-3" />
          <p className="text-charcoal/60">Aucun code de parrainage. Creez-en un pour vos influenceurs.</p>
        </div>
      ) : (
        <div className="card-brutal overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-alt border-b border-charcoal/10">
              <tr>
                <th className="text-left p-3 font-bold uppercase text-[11px]">Code</th>
                <th className="text-left p-3 font-bold uppercase text-[11px]">Influenceur</th>
                <th className="text-right p-3 font-bold uppercase text-[11px]">Reduc</th>
                <th className="text-right p-3 font-bold uppercase text-[11px]">Commission</th>
                <th className="text-right p-3 font-bold uppercase text-[11px]">Usage</th>
                <th className="text-right p-3 font-bold uppercase text-[11px]">A payer</th>
                <th className="text-center p-3 font-bold uppercase text-[11px]">Actif</th>
                <th className="text-right p-3 font-bold uppercase text-[11px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <tr key={c.code} className="border-b border-charcoal/5 hover:bg-surface-alt/50" data-testid={`referral-row-${c.code}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <code className="font-mono font-bold text-primary">{c.code}</code>
                      <button onClick={() => copyCode(c.code)} className="text-charcoal/40 hover:text-charcoal" title="Copier"><Copy className="w-3 h-3" /></button>
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="font-bold">{c.influencer_name}</div>
                    {c.influencer_email && <div className="text-[11px] text-charcoal/50 flex items-center gap-1"><Mail className="w-2.5 h-2.5" />{c.influencer_email}</div>}
                  </td>
                  <td className="p-3 text-right font-bold text-danger">-{c.discount_percent}%</td>
                  <td className="p-3 text-right font-bold text-accent">+{c.reward_percent}%</td>
                  <td className="p-3 text-right">{c.usage_count}{c.max_uses ? ` / ${c.max_uses}` : ''}</td>
                  <td className="p-3 text-right font-bold">{(c.pending_reward || 0).toFixed(2)} EUR</td>
                  <td className="p-3 text-center">
                    {c.is_active !== false ? <CheckCircle className="w-4 h-4 text-secondary inline" /> : <X className="w-4 h-4 text-charcoal/30 inline" />}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button onClick={() => openDetail(c)} className="text-charcoal/60 hover:text-charcoal p-1" title="Details" data-testid={`referral-detail-${c.code}`}><Eye className="w-4 h-4" /></button>
                    {(c.pending_reward || 0) > 0 && (
                      <button onClick={() => markPaid(c.code)} className="text-secondary hover:text-charcoal p-1" title="Marquer paye" data-testid={`referral-pay-${c.code}`}><DollarSign className="w-4 h-4" /></button>
                    )}
                    <button onClick={() => openEdit(c)} className="text-charcoal/60 hover:text-charcoal p-1" title="Editer" data-testid={`referral-edit-${c.code}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => removeCode(c.code)} className="text-danger hover:text-danger/80 p-1" title="Supprimer" data-testid={`referral-delete-${c.code}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form
            className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            data-testid="referral-form"
          >
            <h3 className="font-heading font-bold text-lg mb-4">{editing ? 'Editer le code' : 'Nouveau code de parrainage'}</h3>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-charcoal/60 mb-1 block">Code (sera force en majuscules)</label>
                <input className="input-brutal w-full font-mono" placeholder="INFLU2026" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} required disabled={!!editing} data-testid="referral-form-code" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-charcoal/60 mb-1 block">Nom de l'influenceur</label>
                <input className="input-brutal w-full" value={form.influencer_name} onChange={(e) => setForm({ ...form, influencer_name: e.target.value })} required data-testid="referral-form-name" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-charcoal/60 mb-1 block">Email (optionnel)</label>
                <input type="email" className="input-brutal w-full" value={form.influencer_email} onChange={(e) => setForm({ ...form, influencer_email: e.target.value })} data-testid="referral-form-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-charcoal/60 mb-1 block">Reduction filleul (%)</label>
                  <input type="number" min="0" max="90" step="1" className="input-brutal w-full" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} required data-testid="referral-form-discount" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-charcoal/60 mb-1 block">Commission parrain (%)</label>
                  <input type="number" min="0" max="50" step="1" className="input-brutal w-full" value={form.reward_percent} onChange={(e) => setForm({ ...form, reward_percent: e.target.value })} required data-testid="referral-form-reward" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-charcoal/60 mb-1 block">Usage max (optionnel)</label>
                  <input type="number" min="1" className="input-brutal w-full" placeholder="Illimite" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} data-testid="referral-form-maxuses" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-charcoal/60 mb-1 block">Valable jusqu'au</label>
                  <input type="date" className="input-brutal w-full" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="referral-form-validuntil" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-charcoal/60 mb-1 block">Notes internes</label>
                <textarea className="input-brutal w-full text-sm h-16 resize-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="referral-form-notes" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="referral-form-active" />
                Code actif
              </label>
            </div>
            <div className="flex gap-2 mt-5 justify-end">
              <button type="button" onClick={() => setShowForm(false)} className="btn-outline">Annuler</button>
              <button type="submit" disabled={saving} className="btn-primary" data-testid="referral-form-submit">{saving ? '...' : (editing ? 'Mettre a jour' : 'Creer')}</button>
            </div>
          </form>
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="referral-detail-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-heading font-bold text-lg">Code <code className="font-mono text-primary">{detail.code}</code></h3>
                <p className="text-sm text-charcoal/60">{detail.influencer_name}</p>
              </div>
              <button onClick={() => setDetail(null)} className="text-charcoal/50 hover:text-charcoal"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="card-brutal p-3"><p className="text-[10px] uppercase font-bold text-charcoal/50">Utilisations</p><p className="font-heading font-bold text-xl">{detail.usage_count}</p></div>
              <div className="card-brutal p-3 bg-accent/10"><p className="text-[10px] uppercase font-bold text-charcoal/50">CA total</p><p className="font-heading font-bold text-xl">{(detail.total_revenue || 0).toFixed(2)} EUR</p></div>
              <div className="card-brutal p-3 bg-primary/10"><p className="text-[10px] uppercase font-bold text-charcoal/50">A payer</p><p className="font-heading font-bold text-xl text-danger">{(detail.pending_reward || 0).toFixed(2)} EUR</p></div>
            </div>
            <h4 className="font-bold text-sm mb-2">Historique des utilisations</h4>
            {detailUses.length === 0 ? (
              <p className="text-sm text-charcoal/40 text-center py-6">Aucune utilisation pour le moment.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="border-b border-charcoal/10">
                  <tr>
                    <th className="text-left p-2 font-bold uppercase">Date</th>
                    <th className="text-left p-2 font-bold uppercase">Commande</th>
                    <th className="text-right p-2 font-bold uppercase">Montant</th>
                    <th className="text-right p-2 font-bold uppercase">Reduc</th>
                    <th className="text-right p-2 font-bold uppercase">Commission</th>
                    <th className="text-center p-2 font-bold uppercase">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {detailUses.map((u) => (
                    <tr key={u.use_id} className="border-b border-charcoal/5">
                      <td className="p-2">{u.created_at?.substring(0, 10)}</td>
                      <td className="p-2 font-mono text-[10px]">{u.order_id}</td>
                      <td className="p-2 text-right font-bold">{(u.order_amount || 0).toFixed(2)} EUR</td>
                      <td className="p-2 text-right text-danger">-{(u.discount_amount || 0).toFixed(2)}</td>
                      <td className="p-2 text-right font-bold text-accent">+{(u.reward_amount || 0).toFixed(2)}</td>
                      <td className="p-2 text-center">
                        {u.reward_status === 'paid'
                          ? <span className="text-secondary text-[10px] font-bold">PAYE</span>
                          : <span className="text-danger text-[10px] font-bold">EN ATTENTE</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {(detail.pending_reward || 0) > 0 && (
              <div className="mt-4 flex justify-end">
                <button onClick={() => markPaid(detail.code)} className="btn-primary inline-flex items-center gap-2" data-testid="referral-detail-pay-btn">
                  <DollarSign className="w-4 h-4" /> Marquer {(detail.pending_reward || 0).toFixed(2)} EUR comme payes
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


// ===================== SETTINGS TAB =====================
const SettingsTab = () => {
  const [settings, setSettings] = useState(null);
  const [activeSection, setActiveSection] = useState('toggles');
  const [homepage, setHomepage] = useState(null);
  const [cmsPages, setCmsPages] = useState({ cgu: '', privacy: '', help: '' });
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const [settingsRes, hpRes, pagesRes] = await Promise.all([
        axios.get(`${API}/admin/settings`),
        axios.get(`${API}/admin/cms/homepage`).catch(() => ({ data: { sections: {} } })),
        axios.get(`${API}/admin/cms/pages`).catch(() => ({ data: { cgu: '', privacy: '', help: '' } }))
      ]);
      setSettings(settingsRes.data);
      setHomepage(hpRes.data.sections || {});
      setCmsPages({ cgu: pagesRes.data.cgu || '', privacy: pagesRes.data.privacy || '', help: pagesRes.data.help || '' });
    } catch { toast.error('Erreur chargement parametres'); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const toggleSetting = async (key) => {
    try {
      const newVal = !settings[key];
      await axios.put(`${API}/admin/settings`, { [key]: newVal });
      setSettings({ ...settings, [key]: newVal });
      toast.success('Parametre mis a jour');
    } catch { toast.error('Erreur'); }
  };

  // Homepage state helpers
  const updateSection = (sec, patch) => {
    setHomepage(prev => ({ ...prev, [sec]: { ...(prev[sec] || {}), ...patch } }));
  };
  const updateItem = (sec, listKey, idx, patch) => {
    setHomepage(prev => {
      const list = [...((prev[sec] && prev[sec][listKey]) || [])];
      list[idx] = { ...list[idx], ...patch };
      return { ...prev, [sec]: { ...(prev[sec] || {}), [listKey]: list } };
    });
  };
  const addItem = (sec, listKey, template) => {
    setHomepage(prev => {
      const list = [...((prev[sec] && prev[sec][listKey]) || [])];
      list.push({ ...template });
      return { ...prev, [sec]: { ...(prev[sec] || {}), [listKey]: list } };
    });
  };
  const removeItem = (sec, listKey, idx) => {
    setHomepage(prev => {
      const list = [...((prev[sec] && prev[sec][listKey]) || [])];
      list.splice(idx, 1);
      return { ...prev, [sec]: { ...(prev[sec] || {}), [listKey]: list } };
    });
  };

  const saveHomepage = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/cms/homepage`, { sections: homepage });
      toast.success('Page d\'accueil sauvegardee');
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  // CMS Pages save
  const saveCmsPages = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/cms/pages`, cmsPages);
      toast.success('Pages sauvegardees');
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  if (!settings || !homepage) return <div className="text-center py-12 text-charcoal/50">Chargement...</div>;

  const toggles = [
    { key: 'shop_enabled', label: 'Boutique', description: 'Activer/desactiver la boutique en ligne' },
    { key: 'messaging_enabled', label: 'Messagerie', description: 'Activer/desactiver la messagerie anonyme' },
    { key: 'rewards_enabled', label: 'Recompenses', description: 'Activer/desactiver le systeme de recompenses' },
    { key: 'suggestions_enabled', label: 'Suggestions', description: 'Activer/desactiver les suggestions de produits' },
  ];

  const sections = [
    { id: 'toggles', label: 'Fonctionnalites' },
    { id: 'homepage', label: 'Page d\'accueil' },
    { id: 'pages', label: 'Pages legales & aide' },
  ];

  return (
    <div data-testid="admin-settings-tab">
      {/* Section tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {sections.map((s) => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            className={`text-sm px-4 py-2 rounded-full border-2 font-bold transition-all ${activeSection === s.id ? 'bg-charcoal text-white border-charcoal' : 'bg-white text-charcoal border-charcoal/20 hover:border-charcoal/40'}`}
            data-testid={`settings-section-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Feature Toggles */}
      {activeSection === 'toggles' && (
        <div className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal p-6 max-w-lg">
          <h3 className="font-heading font-bold text-lg mb-6">Feature Toggles</h3>
          <div className="space-y-5">
            {toggles.map((t) => (
              <div key={t.key} className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">{t.label}</p>
                  <p className="text-xs text-charcoal/50">{t.description}</p>
                </div>
                <button
                  onClick={() => toggleSetting(t.key)}
                  data-testid={`toggle-${t.key}`}
                  className={`relative w-12 h-7 rounded-full border-2 border-charcoal transition-colors ${settings[t.key] ? 'bg-secondary' : 'bg-gray-200'}`}
                >
                  <div className={`absolute top-0.5 w-5 h-5 bg-white border-2 border-charcoal rounded-full transition-transform ${settings[t.key] ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Homepage CMS */}
      {activeSection === 'homepage' && (() => {
        const hero = homepage.hero || {};
        const marquee = homepage.marquee || {};
        const ucSec = homepage.use_cases || { items: [] };
        const featuresSec = homepage.features || { items: [] };
        const howSec = homepage.how_it_works || { steps: [] };
        const ctaSec = homepage.cta || {};

        const SectionCard = ({ id, title, subtitle, enabled, onToggle, children }) => (
          <div className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal p-5" data-testid={`hp-section-${id}`}>
            <div className="flex items-center justify-between mb-1">
              <div>
                <h3 className="font-heading font-bold text-base">{title}</h3>
                {subtitle && <p className="text-xs text-charcoal/50">{subtitle}</p>}
              </div>
              <button
                onClick={onToggle}
                data-testid={`hp-toggle-${id}`}
                className={`relative w-11 h-6 rounded-full border-2 border-charcoal transition-colors shrink-0 ${enabled ? 'bg-secondary' : 'bg-gray-200'}`}
                title={enabled ? 'Section affichee' : 'Section masquee'}
              >
                <div className={`absolute top-0.5 w-4 h-4 bg-white border-2 border-charcoal rounded-full transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
            <div className={`mt-3 transition-opacity ${enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
              {children}
            </div>
          </div>
        );

        const Field = ({ label, value, onChange, multiline, placeholder, testid }) => (
          <div>
            <label className="text-[10px] font-bold tracking-[0.1em] uppercase text-charcoal/60 mb-1 block">{label}</label>
            {multiline ? (
              <textarea
                className="input-brutal w-full text-sm h-20 resize-y"
                placeholder={placeholder || 'Vide = valeur par defaut'}
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
              />
            ) : (
              <input
                className="input-brutal w-full text-sm"
                placeholder={placeholder || 'Vide = valeur par defaut'}
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
              />
            )}
          </div>
        );

        return (
          <div className="space-y-4 max-w-4xl">
            <div className="bg-accent/10 border-2 border-accent rounded-xl p-3 text-xs text-charcoal/80">
              💡 Laissez un champ vide pour utiliser la valeur par defaut (traductions). Le toggle masque entierement la section sur la page d'accueil.
            </div>

            {/* HERO */}
            <SectionCard
              id="hero"
              title="Hero (en-tete)"
              subtitle="Premiere zone visible : badge, titre, description, image"
              enabled={hero.enabled !== false}
              onToggle={() => updateSection('hero', { enabled: !(hero.enabled !== false) })}
            >
              <div className="space-y-3">
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 text-xs text-amber-900 mb-2">
                  <strong>Astuce :</strong> les traductions personnalisees dans <em>Gestion des langues</em> ont priorite sur les valeurs ici. Laissez un champ vide pour utiliser la traduction configuree.
                </div>
                <Field label="Badge" value={hero.badge} onChange={(v) => updateSection('hero', { badge: v })} testid="hp-hero-badge" />
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Titre principal" value={hero.title} onChange={(v) => updateSection('hero', { title: v })} testid="hp-hero-title" />
                  <Field label="Mot mis en avant" value={hero.highlight} onChange={(v) => updateSection('hero', { highlight: v })} testid="hp-hero-highlight" />
                </div>
                <Field label="Description" value={hero.desc} onChange={(v) => updateSection('hero', { desc: v })} multiline testid="hp-hero-desc" />
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Texte CTA principal" value={hero.cta_label} onChange={(v) => updateSection('hero', { cta_label: v })} testid="hp-hero-cta" />
                  <Field label="Texte CTA boutique" value={hero.cta_shop_label} onChange={(v) => updateSection('hero', { cta_shop_label: v })} testid="hp-hero-cta-shop" />
                </div>
                <Field label="URL de l'image" value={hero.image_url} onChange={(v) => updateSection('hero', { image_url: v })} placeholder="https://..." testid="hp-hero-image" />
                <div className="grid sm:grid-cols-3 gap-3">
                  <Field label="Stat - chiffre" value={hero.stats_count} onChange={(v) => updateSection('hero', { stats_count: v })} testid="hp-hero-stats-count" />
                  <Field label="Stat - libelle" value={hero.stats_label} onChange={(v) => updateSection('hero', { stats_label: v })} testid="hp-hero-stats-label" />
                  <Field label="Stat - periode" value={hero.stats_period} onChange={(v) => updateSection('hero', { stats_period: v })} testid="hp-hero-stats-period" />
                </div>
              </div>
            </SectionCard>

            {/* MARQUEE */}
            <SectionCard
              id="marquee"
              title="Bandeau defilant"
              subtitle="Texte qui defile sous le hero"
              enabled={marquee.enabled !== false}
              onToggle={() => updateSection('marquee', { enabled: !(marquee.enabled !== false) })}
            >
              <Field label="Texte du bandeau" value={marquee.text} onChange={(v) => updateSection('marquee', { text: v })} testid="hp-marquee-text" />
            </SectionCard>

            {/* USE CASES */}
            <SectionCard
              id="use_cases"
              title="Cas d'usage"
              subtitle="Cartes presentant differents scenarios"
              enabled={ucSec.enabled !== false}
              onToggle={() => updateSection('use_cases', { enabled: !(ucSec.enabled !== false) })}
            >
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <Field label="Tag" value={ucSec.tag} onChange={(v) => updateSection('use_cases', { tag: v })} testid="hp-uc-tag" />
                <Field label="Titre de la section" value={ucSec.title} onChange={(v) => updateSection('use_cases', { title: v })} testid="hp-uc-title" />
              </div>
              <div className="space-y-2">
                {(ucSec.items || []).map((uc, i) => {
                  const IconComp = iconComponents[uc.icon] || MapPin;
                  return (
                    <div key={i} className="border-2 border-charcoal/10 rounded-xl p-3 space-y-2" data-testid={`hp-uc-item-${i}`}>
                      <div className="flex gap-2 items-start">
                        <div className="flex-1 space-y-2">
                          <input
                            className="input-brutal w-full text-sm"
                            placeholder="Titre du cas d'usage"
                            value={uc.title || ''}
                            onChange={(e) => updateItem('use_cases', 'items', i, { title: e.target.value })}
                            data-testid={`hp-uc-title-${i}`}
                          />
                          <textarea
                            className="input-brutal w-full text-sm h-14 resize-none"
                            placeholder="Description"
                            value={uc.desc || ''}
                            onChange={(e) => updateItem('use_cases', 'items', i, { desc: e.target.value })}
                            data-testid={`hp-uc-desc-${i}`}
                          />
                        </div>
                        <div className="flex flex-col items-center gap-1 shrink-0">
                          <div className="w-12 h-12 rounded-lg border-2 border-charcoal/20 flex items-center justify-center" style={uc.iconColor ? { backgroundColor: uc.iconColor } : {}}>
                            <IconComp className="w-5 h-5 text-charcoal" />
                          </div>
                          <button onClick={() => removeItem('use_cases', 'items', i)} className="text-danger hover:text-danger/80 p-1" data-testid={`hp-uc-remove-${i}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {iconOptions.map((opt) => {
                          const Ic = iconComponents[opt.name] || MapPin;
                          return (
                            <button
                              key={opt.name}
                              type="button"
                              onClick={() => updateItem('use_cases', 'items', i, { icon: opt.name })}
                              title={opt.label}
                              className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-all ${uc.icon === opt.name ? 'border-charcoal bg-primary/20' : 'border-charcoal/15 hover:border-charcoal/40 bg-white'}`}
                            >
                              <Ic className="w-3.5 h-3.5" />
                            </button>
                          );
                        })}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {['#FED7D7', '#FEEBC8', '#FEFCBF', '#C6F6D5', '#B2F5EA', '#BEE3F8', '#C3DAFE', '#E9D8FD', '#FED7E2', '#FBD38D'].map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => updateItem('use_cases', 'items', i, { iconColor: c })}
                            className={`w-7 h-7 rounded-full border-2 ${uc.iconColor === c ? 'border-charcoal scale-110' : 'border-charcoal/15'}`}
                            style={{ backgroundColor: c }}
                          />
                        ))}
                      </div>
                      {/* Image upload + URL */}
                      <div className="border-t border-charcoal/10 pt-2 mt-1">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-charcoal/60 mb-1 block">Image (optionnelle)</label>
                        <div className="flex items-start gap-2">
                          <div className="w-16 h-16 shrink-0 rounded-lg border-2 border-charcoal/15 overflow-hidden bg-background flex items-center justify-center">
                            {uc.image ? (
                              <img src={uc.image.startsWith('/api') ? `${API.replace('/api', '')}${uc.image}` : uc.image} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-[10px] text-charcoal/40">Aucune</span>
                            )}
                          </div>
                          <div className="flex-1 space-y-1">
                            <input
                              type="text"
                              className="input-brutal w-full text-xs"
                              placeholder="URL de l'image (https://...)"
                              value={uc.image || ''}
                              onChange={(e) => updateItem('use_cases', 'items', i, { image: e.target.value })}
                              data-testid={`hp-uc-image-url-${i}`}
                            />
                            <div className="flex gap-1.5">
                              <label className="btn-outline text-[10px] px-2 py-1 cursor-pointer flex items-center gap-1" data-testid={`hp-uc-upload-trigger-${i}`}>
                                Choisir un fichier
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={async (e) => {
                                    const f = e.target.files?.[0];
                                    if (!f) return;
                                    const fd = new FormData();
                                    fd.append('file', f);
                                    try {
                                      const { data } = await axios.post(`${API}/upload/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                      updateItem('use_cases', 'items', i, { image: data.url });
                                      toast.success('Image envoyee');
                                    } catch (err) {
                                      toast.error('Echec de l\'upload');
                                    } finally {
                                      e.target.value = '';
                                    }
                                  }}
                                />
                              </label>
                              {uc.image && (
                                <button type="button" onClick={() => updateItem('use_cases', 'items', i, { image: '' })} className="text-[10px] text-danger hover:underline px-1">Retirer</button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <button
                  onClick={() => addItem('use_cases', 'items', { title: '', desc: '', icon: 'MapPin', iconColor: '#FEEBC8' })}
                  className="text-sm font-bold text-charcoal/60 hover:text-charcoal flex items-center gap-1"
                  data-testid="hp-uc-add"
                >
                  <Plus className="w-4 h-4" /> Ajouter un cas d'usage
                </button>
              </div>
            </SectionCard>

            {/* FEATURES */}
            <SectionCard
              id="features"
              title="Fonctionnalites"
              subtitle="Bloc sombre listant les avantages cles"
              enabled={featuresSec.enabled !== false}
              onToggle={() => updateSection('features', { enabled: !(featuresSec.enabled !== false) })}
            >
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <Field label="Tag" value={featuresSec.tag} onChange={(v) => updateSection('features', { tag: v })} testid="hp-feat-tag" />
                <Field label="Titre de la section" value={featuresSec.title} onChange={(v) => updateSection('features', { title: v })} testid="hp-feat-title" />
              </div>
              <div className="space-y-2">
                {(featuresSec.items || []).map((f, i) => {
                  const IconComp = iconComponents[f.icon] || iconComponents.Shield || MapPin;
                  return (
                    <div key={i} className="border-2 border-charcoal/10 rounded-xl p-3" data-testid={`hp-feat-item-${i}`}>
                      <div className="flex gap-2 items-start">
                        <div className="w-10 h-10 rounded-full border-2 border-charcoal bg-primary flex items-center justify-center shrink-0">
                          <IconComp className="w-4 h-4" />
                        </div>
                        <div className="flex-1 space-y-2">
                          <input
                            className="input-brutal w-full text-sm"
                            placeholder="Titre"
                            value={f.title || ''}
                            onChange={(e) => updateItem('features', 'items', i, { title: e.target.value })}
                            data-testid={`hp-feat-title-${i}`}
                          />
                          <textarea
                            className="input-brutal w-full text-sm h-14 resize-none"
                            placeholder="Description"
                            value={f.desc || ''}
                            onChange={(e) => updateItem('features', 'items', i, { desc: e.target.value })}
                            data-testid={`hp-feat-desc-${i}`}
                          />
                        </div>
                        <button onClick={() => removeItem('features', 'items', i)} className="text-danger hover:text-danger/80 p-1 shrink-0" data-testid={`hp-feat-remove-${i}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {iconOptions.map((opt) => {
                          const Ic = iconComponents[opt.name] || MapPin;
                          return (
                            <button
                              key={opt.name}
                              type="button"
                              onClick={() => updateItem('features', 'items', i, { icon: opt.name })}
                              title={opt.label}
                              className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center ${f.icon === opt.name ? 'border-charcoal bg-primary/20' : 'border-charcoal/15 bg-white'}`}
                            >
                              <Ic className="w-3 h-3" />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                <button
                  onClick={() => addItem('features', 'items', { title: '', desc: '', icon: 'Shield' })}
                  className="text-sm font-bold text-charcoal/60 hover:text-charcoal flex items-center gap-1"
                  data-testid="hp-feat-add"
                >
                  <Plus className="w-4 h-4" /> Ajouter une fonctionnalite
                </button>
              </div>
            </SectionCard>

            {/* HOW IT WORKS */}
            <SectionCard
              id="how_it_works"
              title="Comment ca marche"
              subtitle="Etapes numerotees expliquant le fonctionnement"
              enabled={howSec.enabled !== false}
              onToggle={() => updateSection('how_it_works', { enabled: !(howSec.enabled !== false) })}
            >
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <Field label="Tag" value={howSec.tag} onChange={(v) => updateSection('how_it_works', { tag: v })} testid="hp-how-tag" />
                <Field label="Titre de la section" value={howSec.title} onChange={(v) => updateSection('how_it_works', { title: v })} testid="hp-how-title" />
              </div>
              <div className="space-y-2">
                {(howSec.steps || []).map((s, i) => (
                  <div key={i} className="border-2 border-charcoal/10 rounded-xl p-3" data-testid={`hp-how-step-${i}`}>
                    <div className="flex gap-2 items-start">
                      <span className="text-2xl font-black text-charcoal/30 shrink-0 w-8">{String(i + 1).padStart(2, '0')}</span>
                      <div className="flex-1 space-y-2">
                        <input
                          className="input-brutal w-full text-sm"
                          placeholder="Titre de l'etape"
                          value={s.title || ''}
                          onChange={(e) => updateItem('how_it_works', 'steps', i, { title: e.target.value })}
                          data-testid={`hp-how-title-${i}`}
                        />
                        <textarea
                          className="input-brutal w-full text-sm h-14 resize-none"
                          placeholder="Description"
                          value={s.desc || ''}
                          onChange={(e) => updateItem('how_it_works', 'steps', i, { desc: e.target.value })}
                          data-testid={`hp-how-desc-${i}`}
                        />
                      </div>
                      <button onClick={() => removeItem('how_it_works', 'steps', i)} className="text-danger hover:text-danger/80 p-1 shrink-0" data-testid={`hp-how-remove-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => addItem('how_it_works', 'steps', { title: '', desc: '' })}
                  className="text-sm font-bold text-charcoal/60 hover:text-charcoal flex items-center gap-1"
                  data-testid="hp-how-add"
                >
                  <Plus className="w-4 h-4" /> Ajouter une etape
                </button>
              </div>
            </SectionCard>

            {/* CTA */}
            <SectionCard
              id="cta"
              title="Appel a l'action final"
              subtitle="Carte orange en bas de page"
              enabled={ctaSec.enabled !== false}
              onToggle={() => updateSection('cta', { enabled: !(ctaSec.enabled !== false) })}
            >
              <div className="space-y-3">
                <Field label="Titre" value={ctaSec.title} onChange={(v) => updateSection('cta', { title: v })} testid="hp-cta-title" />
                <Field label="Description" value={ctaSec.desc} onChange={(v) => updateSection('cta', { desc: v })} multiline testid="hp-cta-desc" />
                <Field label="Texte du bouton" value={ctaSec.button_label} onChange={(v) => updateSection('cta', { button_label: v })} testid="hp-cta-btn" />
              </div>
            </SectionCard>

            <div className="sticky bottom-4 flex justify-end pt-2">
              <button onClick={saveHomepage} disabled={saving} className="btn-primary shadow-brutal-lg" data-testid="hp-save-btn">
                {saving ? 'Sauvegarde...' : 'Sauvegarder la page d\'accueil'}
              </button>
            </div>
          </div>
        );
      })()}

      {/* Legal Pages CMS */}
      {activeSection === 'pages' && (
        <div className="space-y-6">
          {[
            { key: 'cgu', label: 'Conditions Generales d\'Utilisation (CGU)', placeholder: 'Redigez vos conditions generales d\'utilisation...' },
            { key: 'privacy', label: 'Politique de Confidentialite', placeholder: 'Redigez votre politique de confidentialite...' },
            { key: 'help', label: 'Aide', placeholder: 'Redigez le contenu de la page d\'aide...' },
          ].map((p) => (
            <div key={p.key} className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal p-6">
              <h3 className="font-heading font-bold text-sm mb-3">{p.label}</h3>
              <textarea
                className="input-brutal w-full text-sm h-40 resize-y"
                placeholder={p.placeholder}
                value={cmsPages[p.key]}
                onChange={(e) => setCmsPages(prev => ({ ...prev, [p.key]: e.target.value }))}
                data-testid={`cms-page-${p.key}`}
              />
              <p className="text-xs text-charcoal/40 mt-1">Supporte le format texte simple. Les retours a la ligne sont preserves.</p>
            </div>
          ))}
          <button onClick={saveCmsPages} disabled={saving} className="btn-primary" data-testid="save-cms-pages-btn">
            {saving ? 'Sauvegarde...' : 'Sauvegarder les pages'}
          </button>
        </div>
      )}
    </div>
  );
};

// ===================== PAGINATION =====================
const Pagination = ({ page, setPage, total, limit }) => {
  const maxPage = Math.max(Math.ceil(total / limit) - 1, 0);
  if (total <= limit) return null;
  return (
    <div className="flex items-center justify-center gap-3 mt-4">
      <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="btn-outline text-sm py-1.5 px-3 disabled:opacity-30" data-testid="pagination-prev"><ChevronLeft className="w-4 h-4" /></button>
      <span className="text-sm text-charcoal/60">{page + 1} / {maxPage + 1}</span>
      <button disabled={page >= maxPage} onClick={() => setPage(p => p + 1)} className="btn-outline text-sm py-1.5 px-3 disabled:opacity-30" data-testid="pagination-next"><ChevronRight className="w-4 h-4" /></button>
    </div>
  );
};

// ===================== MAIN ADMIN DASHBOARD =====================
const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/admin/stats`);
        setStats(res.data);
      } catch (err) {
        console.error('Failed to load admin stats', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl sm:text-4xl text-charcoal">Backoffice Admin</h1>
          <p className="text-charcoal/60 mt-1">Tableau de bord d'administration Tagora</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 overflow-x-auto pb-2 mb-8 -mx-4 px-4 sm:mx-0 sm:px-0">
          {tabs.map((t) => {
            const Icon = t.icon;
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap border-2 transition-all ${
                  isActive
                    ? 'bg-charcoal text-white border-charcoal shadow-brutal-primary'
                    : 'bg-white text-charcoal/70 border-charcoal/20 hover:border-charcoal/50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && <OverviewTab stats={stats} />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'orders' && <OrdersTab />}
        {activeTab === 'products' && <ProductsTab />}
        {activeTab === 'pingmes' && <PingMesTab />}
        {activeTab === 'organizations' && <OrganizationsTab />}
        {activeTab === 'generate' && <GenerateTab />}
        {activeTab === 'suggestions' && <SuggestionsTab />}
        {activeTab === 'b2b' && <B2BLeadsTab />}
        {activeTab === 'shipping' && <ShippingTab />}
        {activeTab === 'referrals' && <ReferralsTab />}
        {activeTab === 'settings' && <SettingsTab />}
        {activeTab === 'deploy' && <DeployTab />}
        {activeTab === 'translations' && <TranslationsTab />}
      </div>
    </div>
  );
};

export default AdminDashboard;
